import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyGetRelationsQuery } from "../../../store/apiSlice/relationshipApiSlice";
import { accentSurfaces } from "./graphColors";
import { computeNodeDepths } from "./graphLayoutUtils";
import { configForNodeType } from "./nodeTypeConfig";
import { layoutRelationshipGraph } from "./radialLayout";
import { useForceDirectedPositions } from "./useForceDirectedPositions";

/**
 * Links are undirected for display: A→B and B→A are the same line.
 *
 * @param {string} a
 * @param {string} b
 * @return {string}
 */
export function linkPairKey(a, b) {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * @param {{type: string, id: number, label?: string}|null} anchor
 * @param {{onOpenFiles?: function, onOpenTasks?: function}|null} handlers
 */
export function useRelationshipGraph(anchor, handlers) {
  const [fetchRelations, { isFetching, error }] = useLazyGetRelationsQuery();
  /** @type {[Map<string, object>, function]} */
  const [nodesByKey, setNodesByKey] = useState(() => new Map());
  /** @type {[Array<object>, function]} */
  const [edges, setEdges] = useState([]);
  /** @type {[Set<string>, function]} */
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  /** @type {[Set<string>, function]} */
  const [loadingKeys, setLoadingKeys] = useState(() => new Set());
  const [focusKey, setFocusKey] = useState(null);
  const [enteringKeys, setEnteringKeys] = useState(() => new Set());
  const [animatingEdgeIds, setAnimatingEdgeIds] = useState(() => new Set());
  /** @type {[{key: string, added: number}|null, function]} */
  const [expandNotice, setExpandNotice] = useState(null);
  const knownNodeKeysRef = useRef(new Set());
  const knownEdgeIdsRef = useRef(new Set());
  const nodesByKeyRef = useRef(nodesByKey);
  nodesByKeyRef.current = nodesByKey;

  const mergeSlice = useCallback((payload, parentKey) => {
    if (!payload?.nodes?.length) {
      return;
    }
    setNodesByKey(prev => {
      const next = new Map(prev);
      payload.nodes.forEach(node => {
        next.set(node.key, node);
      });
      return next;
    });
    setEdges(prev => {
      const seen = new Set(prev.map(edge => linkPairKey(edge.source, edge.target)));
      const additions = (payload.edges || []).filter(edge => {
        const token = linkPairKey(edge.source, edge.target);
        if (seen.has(token)) {
          return false;
        }
        seen.add(token);
        return true;
      });
      return additions.length ? [...prev, ...additions] : prev;
    });
    const expandedKey = parentKey || payload.nodes[0]?.key;
    if (expandedKey) {
      setExpandedKeys(prev => new Set(prev).add(expandedKey));
    }
  }, []);

  const loadNode = useCallback(
    async (type, id, parentKey = null) => {
      const nodeKey = `${type}:${id}`;
      setLoadingKeys(prev => new Set(prev).add(nodeKey));
      setExpandNotice(null);
      try {
        const payload = await fetchRelations({ type, id }).unwrap();
        const added = (payload?.nodes || []).filter(
          node => !nodesByKeyRef.current.has(node.key),
        ).length;
        mergeSlice(payload, parentKey);
        setFocusKey(nodeKey);
        if (parentKey) {
          setExpandNotice({ key: nodeKey, added });
        }
      } catch {
        // The query hook's `error` drives the drawer alert.
      } finally {
        setLoadingKeys(prev => {
          const next = new Set(prev);
          next.delete(nodeKey);
          return next;
        });
      }
    },
    [fetchRelations, mergeSlice],
  );

  useEffect(() => {
    if (!anchor?.type || !anchor?.id) {
      setNodesByKey(new Map());
      setEdges([]);
      setExpandedKeys(new Set());
      setFocusKey(null);
      setExpandNotice(null);
      setEnteringKeys(new Set());
      setAnimatingEdgeIds(new Set());
      knownNodeKeysRef.current = new Set();
      knownEdgeIdsRef.current = new Set();
      return;
    }
    setNodesByKey(new Map());
    setEdges([]);
    setExpandedKeys(new Set());
    setEnteringKeys(new Set());
    setAnimatingEdgeIds(new Set());
    knownNodeKeysRef.current = new Set();
    knownEdgeIdsRef.current = new Set();
    loadNode(anchor.type, anchor.id, null);
  }, [anchor?.type, anchor?.id, loadNode]);

  useEffect(() => {
    const currentKeys = new Set(nodesByKey.keys());
    const newKeys = [...currentKeys].filter(key => !knownNodeKeysRef.current.has(key));
    if (newKeys.length) {
      setEnteringKeys(new Set(newKeys));
      const timer = window.setTimeout(() => setEnteringKeys(new Set()), 900);
      knownNodeKeysRef.current = currentKeys;
      return () => window.clearTimeout(timer);
    }
    knownNodeKeysRef.current = currentKeys;
    return undefined;
  }, [nodesByKey]);

  useEffect(() => {
    const currentEdgeIds = new Set(
      edges.map(edge => `${edge.source}|${edge.target}|${edge.relation || "related"}`),
    );
    const newEdgeIds = [...currentEdgeIds].filter(id => !knownEdgeIdsRef.current.has(id));
    if (newEdgeIds.length) {
      setAnimatingEdgeIds(new Set(newEdgeIds));
      const timer = window.setTimeout(() => setAnimatingEdgeIds(new Set()), 900);
      knownEdgeIdsRef.current = currentEdgeIds;
      return () => window.clearTimeout(timer);
    }
    knownEdgeIdsRef.current = currentEdgeIds;
    return undefined;
  }, [edges]);

  const rootKey = anchor ? `${anchor.type}:${anchor.id}` : null;

  const handleNodeOpen = useCallback(
    node => {
      if (!node) {
        return;
      }
      if (node.type === "files" || node.type === "tasks") {
        const ownerId = node.meta?.anchor_id;
        const ownedByAnchor =
          node.meta?.anchor_type === anchor?.type && ownerId === anchor?.id;
        if (ownedByAnchor && node.type === "files" && handlers?.onOpenFiles) {
          handlers.onOpenFiles(ownerId, anchor?.label || "", "");
          return;
        }
        if (ownedByAnchor && node.type === "tasks" && handlers?.onOpenTasks) {
          handlers.onOpenTasks(ownerId, anchor?.label || "", false, node.meta.count || 0);
          return;
        }
        const ownerConfig = configForNodeType(node.meta?.anchor_type || "");
        if (ownerConfig.path && ownerId != null) {
          handlers?.onOpenRecord?.(ownerConfig.path(ownerId), node);
        }
        return;
      }
      if (node.type === "prepayment" && node.id != null) {
        handlers?.onOpenPrepayment?.(node.id);
        return;
      }
      const config = configForNodeType(node.type);
      if (config.path && node.id != null) {
        handlers?.onOpenRecord?.(config.path(node.id), node);
      }
    },
    [anchor?.id, anchor?.label, anchor?.type, handlers],
  );

  const handleNodeClick = useCallback(
    node => {
      if (node.type === "files" || node.type === "tasks") {
        handleNodeOpen(node);
        return;
      }
      if (!node.expandable || node.type === "more") {
        return;
      }
      if (expandedKeys.has(node.key)) {
        setFocusKey(node.key);
        setExpandNotice(null);
        return;
      }
      if (node.id == null) {
        return;
      }
      loadNode(node.type, node.id, node.key);
    },
    [expandedKeys, handleNodeOpen, loadNode],
  );

  const anchorPositions = useMemo(() => {
    if (!rootKey) {
      return {};
    }
    return layoutRelationshipGraph(rootKey, edges, expandedKeys);
  }, [edges, expandedKeys, rootKey]);

  const nodeIds = useMemo(() => Array.from(nodesByKey.keys()), [nodesByKey]);

  const { positions, isSimulating } = useForceDirectedPositions(
    anchorPositions,
    edges,
    rootKey,
    nodeIds,
  );

  const depths = useMemo(
    () => (rootKey ? computeNodeDepths(rootKey, edges) : {}),
    [edges, rootKey],
  );

  const flowNodes = useMemo(() => {
    let enterIndex = 0;
    return Array.from(nodesByKey.values()).map(node => {
      const position = positions[node.key] || { x: 0, y: 0 };
      const isEntering = enteringKeys.has(node.key);
      const enterDelay = isEntering ? enterIndex++ * 45 : 0;
      return {
        id: node.key,
        type: "relation",
        position,
        data: {
          node,
          isRoot: node.key === rootKey,
          isFocus: node.key === focusKey,
          isExpanded: expandedKeys.has(node.key),
          isLoading: loadingKeys.has(node.key),
          isEntering,
          enterDelay,
          depth: depths[node.key] ?? 1,
          onNodeClick: handleNodeClick,
          onNodeOpen: handleNodeOpen,
        },
      };
    });
  }, [
    depths,
    enteringKeys,
    expandedKeys,
    focusKey,
    handleNodeClick,
    handleNodeOpen,
    loadingKeys,
    nodesByKey,
    positions,
    rootKey,
  ]);

  const flowEdges = useMemo(
    () =>
      edges.map(edge => {
        const id = `${edge.source}|${edge.target}|${edge.relation || "related"}`;
        const targetNode = nodesByKey.get(edge.target);
        const accent = targetNode
          ? accentSurfaces(configForNodeType(targetNode.type).color)
          : null;
        return {
          id,
          source: edge.source,
          target: edge.target,
          type: "relation",
          data: {
            animate: animatingEdgeIds.has(id),
            stroke: accent?.edgeStroke,
            strokeMuted: accent?.edgeStrokeMuted,
          },
        };
      }),
    [animatingEdgeIds, edges, nodesByKey],
  );

  return {
    flowNodes,
    flowEdges,
    isFetching,
    error,
    focusKey,
    rootKey,
    isSimulating,
    handleNodeOpen,
    expandNotice,
  };
}
